import qs from 'qs';
import { retailerSettingsService } from '../retailer-settings/retailer-settings';
import {
  clearReturnSubmission,
  RECEIVE_LABEL_URL,
  receiveMultiLabel,
  receivePrinterlessInfoLink,
  receiveReturnSubmission,
} from '../../actions/returnSubmission';
import { orderService } from '../order-service/order-service';
import { logData } from '../../actions/analytics';
import { EVENT_VALUES, EVENTS } from '../../constants/noflake/noflakeSchema';
import getEscapedOrderNumber from '../../lib/GetEscapedOrderNumber';
import { PRINTERLESS_METHODS } from '../../constants/returnMethods';
import { titan } from '../../lib/productAnalytics';
import { PRODUCT_LIST_IDS, TITAN_EVENTS } from '../../constants/titan';
import { modifyUrlParams } from '../../lib/url';
import { isDeferredOverride } from '../../lib/storeAssociate';
import { hasMultiOrder } from '../../react-components/AdditionalOrderForm/utils';
import { getOrderWithMultiPayload } from '../../react-components/OrderSubmitButtons/utils';
import { MULTI_ORDER_RETURN_SUBMIT, BORIS_STORE_DROP_OFF } from '../../constants/endpoints';
import { ERRORS_WITH_CUSTOM_MESSAGES } from '../../constants/errorCodes';
import { store } from '../../store';

const removeHashKey = (obj) => {
  // TODO: [ANGULAR-REFACTOR] This won't be needed once we refactor Angular
  // Angular adds a $$hashKey to objects in ng-repeat
  // During the Angular $http call, the $$hashKey is removed before sending the data
  if (Array.isArray(obj)) {
    return obj.map((item) => removeHashKey(item));
  } else if (obj !== null && typeof obj === 'object') {
    const { $$hashKey, ...rest } = obj;
    return Object.fromEntries(
      Object.entries(rest).map(([key, value]) => [key, removeHashKey(value)]),
    );
  }
  return obj;
};

const postReturnServiceFunction = () => {
  const http = window.location.protocol;
  const slashes = http.concat('//');
  const hostURL = slashes.concat(window.location.hostname);
  const disabled = retailerSettingsService.getDisabled();
  const product = retailerSettingsService.getProduct();
  const apiVersion = retailerSettingsService.getSettings().api_version || 3;
  const RETAILER = disabled ? 'peninsula' : retailerSettingsService.getName();
  let returnInfo = {};
  let labelUrl = '';

  const submitReturn = (order) => {
    const {
      page: { locale, productType, browserFingerprint, initialPageLoadParams, deferOverride },
      retailerSettings: { name: retailerName } = {},
      returnOptions: { returnOptions, selectedOptionId },
      returnsSettings: {
        [productType]: { refunds: { enable_api: refundApiEnabled = false } = {} } = {},
      } = {},
      returnItems: { hasExchangesOnly } = {},
    } = store.getState();

    store.dispatch(clearReturnSubmission());
    const itemsReturnedByDate = orderService.getReturnAndEligibleByDate(true);
    itemsReturnedByDate.forEach(({ ship_by_date: date, items }) => {
      items.forEach((item) => {
        if (item.return_qty <= 0) return;
        const existingItem = order.return_items.find(
          ({ product_sku }) => product_sku == item.product_sku,
        );
        existingItem.shipments = existingItem.shipments || [];
        existingItem.shipments.push({
          item_id: parseInt(item.item_id, 10),
          product_sku: item.product_sku,
          ship_by_date: item.ship_by_date,
          quantity: item.return_qty,
          ship_id: item.ship_id,
        });
      });
    });

    let LOCALE = locale;
    LOCALE = LOCALE === 'en_EU' ? 'en_BE' : LOCALE;
    const productInfoType = product === 'repairs' ? 'repairinfo' : 'returninfo';
    const url = new URL(`${hostURL}/returns/${RETAILER}/${productInfoType}`);
    const params = {
      version: apiVersion,
      locale: LOCALE,
    };
    const queryString = new URLSearchParams(params).toString();
    const postURL = `${url}?${queryString}`;

    if (refundApiEnabled && !hasExchangesOnly && !order.refund_option) {
      store.dispatch(
        logData({
          type: EVENTS.DEBUG,
          value: EVENT_VALUES[EVENTS.DEBUG].refund_option_missing,
        }),
      );
    }

    if (deferOverride) {
      order['return_override_code'] = null;
    }

    if (initialPageLoadParams?.mode) {
      order.role = initialPageLoadParams.mode;
    }

    const handleResponse = (response) => {
      store.dispatch(receiveReturnSubmission(response.data));

      if (
        response.data.error_response &&
        response.data.error_response.error_code === 'returns.submit.async'
      ) {
        return response;
      }

      if (
        response.data.error_response &&
        ERRORS_WITH_CUSTOM_MESSAGES.includes(response.data.error_response.error_code)
      ) {
        throw new Error(response.data.error_response.error_code);
      }

      if (response.data.return_process_info && product === 'returns') {
        returnInfo = response.data.return_process_info;
        returnInfo.order_number = getEscapedOrderNumber(returnInfo.order_number);
        labelUrl = `${hostURL}/returns/${RETAILER}/viewlabel?return_id=${returnInfo.encrypted_return_id}`;

        if (response.data.return_process_infos) {
          store.dispatch(
            receiveMultiLabel({ packagesCount: response.data.return_process_infos.length }),
          );
        }

        store.dispatch({
          type: RECEIVE_LABEL_URL,
          data: {
            labelUrl,
          },
        });

        if (returnInfo.is_qrcode_present || PRINTERLESS_METHODS.includes(order.return_method)) {
          store.dispatch(receivePrinterlessInfoLink(returnInfo.encrypted_return_id));
        }

        if (returnOptions && selectedOptionId) {
          const selectedOption =
            returnOptions.find((option) => option.id === selectedOptionId) || {};
          titan.event(TITAN_EVENTS.PRODUCT_ADDED, {
            order_id: order.order_number,
            product_id: selectedOptionId,
            list_id: PRODUCT_LIST_IDS.PUDO_OPTIONS,
            variant: selectedOption.display_method_user_preference,
          });
        }

        if (isDeferredOverride(initialPageLoadParams)) {
          handleBORISStoreDropOff(returnInfo.encrypted_return_id);
        }

        return response;
      }

      if (response.data.repair_process_info && product === 'repairs') {
        return response;
      }

      const {
        data: {
          error_response: errorResponse,
          error_response: { error_message_detail_code: errorMsg = errorResponse } = {},
        } = {},
      } = response;

      throw new Error(errorMsg);
    };

    const handleBORISStoreDropOff = (return_id) =>
      fetch(BORIS_STORE_DROP_OFF(retailerName), {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json; charset=utf-8',
          'narvar-tfid': browserFingerprint,
        },
        body: JSON.stringify({
          return_id,
        }),
      });

    const handleError = (error) => {
      console.error(error); // eslint-disable-line
      if (error.status === 302 || error.status === 403) {
        modifyUrlParams({
          paramsToDelete: ['init'],
          paramsToAppend: [['expired', 'true']],
        });
        window.location.reload();
      }
      throw new Error(error.message);
    };

    if (hasMultiOrder({ returnItems: order.return_items })) {
      const {
        page = {},
        page: { locale },
      } = store.getState();

      const body = getOrderWithMultiPayload({
        page,
        returnItems: order.return_items,
        confirmOrder: order,
      });

      return fetch(
        MULTI_ORDER_RETURN_SUBMIT({
          retailerName,
          params: qs.stringify({
            locale,
            version: 3,
          }),
        }),
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            'narvar-tfid': browserFingerprint,
          },
          body: JSON.stringify(body),
        },
      )
        .then((e) => e.json())
        .then((e) => handleResponse({ data: e }))
        .catch(handleError);
    }

    return fetch(postURL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json; charset=utf-8',
        'narvar-tfid': browserFingerprint,
      },
      body: JSON.stringify(removeHashKey(order)),
    })
      .then(async (response) => {
        const data = await response.json(); // Assuming the server responds with JSON
        const res = {
          data,
          status: response.status,
          headers: response.headers,
          config: {
            method: 'POST',
            headers: {
              Accept: 'application/json',
              'Content-Type': 'application/json; charset=utf-8',
              'narvar-tfid': browserFingerprint,
            },
            body: JSON.stringify(order),
            url: postURL,
          },
          statusText: response.statusText,
          xhrStatus: 'complete',
        };
        if (res.status < 200 || res.status > 299) {
          // Taken from Angular's docs: https://docs.angularjs.org/api/ng/service/$http#$http-returns
          // If the status is not in the 200s, throw the response object
          throw res;
        }
        return res;
      })
      .then(handleResponse)
      .catch(handleError);
  };

  const setReturnInfo = (data) => {
    returnInfo = data;
  };

  const getReturn = () => returnInfo;

  const getLabel = () => labelUrl;

  const setLabelUrl = (url) => {
    labelUrl = url;
  };

  return {
    submitReturn,
    getReturn,
    getLabel,
    setLabelUrl,
    setReturnInfo,
  };
};

export const postReturnService = postReturnServiceFunction();
